/**
 * SEO & Metadata Manager for asesoriasem.com
 * Manages dynamic document title, meta tags, Open Graph, Twitter Cards,
 * Canonical links and Schema.org JSON-LD structured data.
 */

const BASE_URL = 'https://asesoriasem.com';
const BRAND_NAME = 'EM Asesoría Inmobiliaria y Jurídica';
const LEGAL_REPRESENTATIVE = 'Licenciado Edgar Molina';
const PHONE_INTL = '+526272792964';
const PHONE_LOCAL = '6272792964';
const EMAIL_OFFICIAL = 'juridico@asesoriasem.com';
const DEFAULT_IMAGE = `${BASE_URL}/EM%20Asesorias.png`;

const SOCIAL_LINKS = [
  'https://www.facebook.com/profile.php?id=61578869614923',
  'https://www.instagram.com/emasesoria/'
];

/**
 * Base Schema.org for RealEstateAgent & LegalService
 */
export const getBaseOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': ['RealEstateAgent', 'LegalService'],
    '@id': `${BASE_URL}/#organization`,
    'name': BRAND_NAME,
    'legalName': LEGAL_REPRESENTATIVE,
    'url': BASE_URL,
    'logo': DEFAULT_IMAGE,
    'image': DEFAULT_IMAGE,
    'telephone': PHONE_INTL,
    'email': EMAIL_OFFICIAL,
    'priceRange': 'MXN',
    'currenciesAccepted': 'MXN',
    'paymentAccepted': 'Cash, Credit Card, Bank Transfer, Mortgage Loan',
    'founder': {
      '@type': 'Person',
      'name': LEGAL_REPRESENTATIVE,
      'jobTitle': 'Licenciado en Derecho / Asesor Inmobiliario y Jurídico',
      'telephone': PHONE_INTL,
      'email': EMAIL_OFFICIAL
    },
    'attorney': {
      '@type': 'Person',
      'name': LEGAL_REPRESENTATIVE,
      'jobTitle': 'Licenciado en Derecho / Consultor Jurídico'
    },
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Hidalgo del Parral',
      'addressRegion': 'Chihuahua',
      'postalCode': '33800',
      'addressCountry': 'MX'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': 26.9317,
      'longitude': -105.6664
    },
    'areaServed': [
      {
        '@type': 'AdministrativeArea',
        'name': 'Hidalgo del Parral, Chihuahua'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Sur del Estado de Chihuahua'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Santa Bárbara, Chihuahua'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'San Francisco del Oro, Chihuahua'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Valle de Allende, Chihuahua'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Matamoros, Chihuahua'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Jiménez, Chihuahua'
      }
    ],
    'sameAs': SOCIAL_LINKS,
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Servicios Inmobiliarios y Jurídicos',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Compraventa y Consignación de Inmuebles en Parral'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Arrendamiento Seguro con Pólizas Jurídicas y Protección por Ley de Extinción de Dominio'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Trámites Notariales, Escrituración y Cancelación de Hipotecas'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Juicios Sucesorios Intestamentarios y Regularización de Predios'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Asesoría en Créditos Infonavit, FOVISSSTE y Bancarios'
          }
        }
      ]
    }
  };
};

/**
 * Person Schema for Lic. Edgar Molina (About / Nosotros page)
 */
export const getPersonSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${BASE_URL}/#lic-edgar-molina`,
    'name': 'Lic. Edgar Molina',
    'alternateName': 'Edgar Molina',
    'jobTitle': 'Licenciado en Derecho / Consultor Jurídico y Asesor Inmobiliario',
    'telephone': PHONE_INTL,
    'email': EMAIL_OFFICIAL,
    'image': DEFAULT_IMAGE,
    'url': `${BASE_URL}/#about`,
    'worksFor': {
      '@type': ['RealEstateAgent', 'LegalService'],
      'name': BRAND_NAME,
      'url': BASE_URL
    },
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Hidalgo del Parral',
      'addressRegion': 'Chihuahua',
      'postalCode': '33800',
      'addressCountry': 'MX'
    },
    'sameAs': SOCIAL_LINKS
  };
};

/**
 * Generates Schema.org JSON-LD for Property Detail
 */
export const getPropertySchema = (property) => {
  if (!property) return null;

  const effectivePrice = Number(property.priceMXN || property.price || 0);
  const isRent = (property.operationType || property.operation) === 'Renta' || (property.operationType || property.operation) === 'renta';
  const neighborhood = property.location?.neighborhood || property.location?.colonia || 'Parral';
  const city = property.location?.city || 'Hidalgo del Parral';
  const images = Array.isArray(property.images) && property.images.length > 0
    ? property.images
    : [DEFAULT_IMAGE];

  const fullAddress = `${property.location?.street || ''} ${property.location?.exteriorNumber || ''}, Col. ${neighborhood}, ${city}, Chihuahua, México`.trim();

  const propertyUrl = `${BASE_URL}/#propiedad/${property.slug || property.id}`;

  let placeType = 'SingleFamilyResidence';
  const rawType = (property.propertyType || property.type || '').toLowerCase();
  if (rawType.includes('terreno')) {
    placeType = 'Landform';
  } else if (rawType.includes('departamento')) {
    placeType = 'Apartment';
  } else if (rawType.includes('local') || rawType.includes('bodega') || rawType.includes('comercial')) {
    placeType = 'CommercialProperties';
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    '@id': propertyUrl,
    'url': propertyUrl,
    'name': property.title,
    'description': property.description || `${property.title} en ${neighborhood}, ${city}. ${isRent ? 'Renta' : 'Venta'} con certeza jurídica y respaldo legal en Parral.`,
    'image': images,
    'datePosted': property.createdAt || '2026-01-01',
    'offers': {
      '@type': 'Offer',
      'price': effectivePrice,
      'priceCurrency': 'MXN',
      'availability': (property.status === 'vendida' || property.status === 'rentada')
        ? 'https://schema.org/SoldOut'
        : 'https://schema.org/InStock',
      'businessFunction': isRent
        ? 'http://purl.org/goodrelations/v1#LeaseOut'
        : 'http://purl.org/goodrelations/v1#Sell',
      'priceSpecification': {
        '@type': 'UnitPriceSpecification',
        'price': effectivePrice,
        'priceCurrency': 'MXN',
        'unitText': isRent ? 'Mensual' : 'Total'
      },
      'seller': {
        '@type': ['RealEstateAgent', 'LegalService'],
        'name': BRAND_NAME,
        'telephone': PHONE_INTL,
        'email': EMAIL_OFFICIAL,
        'url': BASE_URL
      }
    },
    'about': {
      '@type': placeType,
      'name': property.title,
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': property.location?.street || neighborhood,
        'addressLocality': city,
        'addressRegion': 'Chihuahua',
        'postalCode': property.location?.postalCode || '33800',
        'addressCountry': 'MX'
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': property.location?.coordinates?.lat || property.location?.lat || 26.9317,
        'longitude': property.location?.coordinates?.lng || property.location?.lng || -105.6664
      },
      ...(property.features?.bedrooms ? { 'numberOfRooms': property.features.bedrooms } : {}),
      ...(property.features?.bathrooms || property.features?.fullBathrooms ? {
        'numberOfBathroomsTotal': Number(property.features.fullBathrooms || property.features.bathrooms || 0) + (Number(property.features?.halfBathrooms || 0) * 0.5)
      } : {}),
      ...(property.features?.constructionAreaM2 || property.features?.buildSizeM2 ? {
        'floorSize': {
          '@type': 'QuantitativeValue',
          'value': property.features.constructionAreaM2 || property.features.buildSizeM2,
          'unitCode': 'MTK'
        }
      } : {})
    }
  };
};

/**
 * Generates Schema.org JSON-LD for FAQ Page
 */
export const getFaqSchema = (faqItems = []) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${BASE_URL}/#guide`,
    'mainEntity': faqItems.map((item) => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer
      }
    }))
  };
};

/**
 * Helper to update or create a meta tag by name or property
 */
const setMetaTag = (attributeName, attributeValue, content) => {
  if (!content) return;
  let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

/**
 * Helper to update or create canonical link
 */
const setCanonicalUrl = (url) => {
  let element = document.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
};

/**
 * Helper to inject Schema.org JSON-LD script into <head>
 */
const setStructuredData = (schemaObj) => {
  const SCRIPT_ID = 'structured-data-jsonld';
  let script = document.getElementById(SCRIPT_ID);
  if (!script) {
    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemaObj);
};

/**
 * Main SEO updater function
 */
export const updatePageSEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData
}) => {
  // Title
  if (title) {
    document.title = title;
  }

  // Descriptions & Keywords
  if (description) {
    setMetaTag('name', 'description', description);
  }
  if (keywords) {
    setMetaTag('name', 'keywords', keywords);
  }

  // Canonical
  const activeCanonical = canonicalUrl || ogUrl || BASE_URL;
  setCanonicalUrl(activeCanonical);

  // Open Graph
  setMetaTag('property', 'og:site_name', BRAND_NAME);
  setMetaTag('property', 'og:locale', 'es_MX');
  setMetaTag('property', 'og:type', ogType);
  setMetaTag('property', 'og:title', ogTitle || title);
  setMetaTag('property', 'og:description', ogDescription || description);
  setMetaTag('property', 'og:url', ogUrl || activeCanonical);
  setMetaTag('property', 'og:image', ogImage || DEFAULT_IMAGE);

  // Twitter Cards
  setMetaTag('name', 'twitter:card', twitterCard);
  setMetaTag('name', 'twitter:title', ogTitle || title);
  setMetaTag('name', 'twitter:description', ogDescription || description);
  setMetaTag('name', 'twitter:image', ogImage || DEFAULT_IMAGE);

  // Structured Data (JSON-LD)
  if (structuredData) {
    setStructuredData(structuredData);
  } else {
    setStructuredData(getBaseOrganizationSchema());
  }
};
